import React, { useState, useEffect, useMemo, memo } from "react";
import { fetchCategory } from "../../../domain/usecases/fetchCategoryProduct"; // Import the fetch function
import SpinnerLoading from "../../components/spinnerLoading/SpinnerLoading";
import { CategoryProduct } from "../../../domain/entities/CategoryProduct";
import { sendRegister } from "../../../domain/usecases/fetchRegister";
import logoLM from "../../../assets/Logo/logo.png";
import TabsComponent from "../../components/selectProductComponents/TabComponents";
import CascadingDropdown from "../../components/selectDropdownComponents/CascadingDropdown";
import { useNavigate } from 'react-router-dom';
import { ProductItem } from "../../../domain/entities/ProductItem";
import ListProductsRegister from "../../components/selectProductComponents/ListProductsRegister";
import { PayloadRegister } from "../../../domain/entities/PayloadRegister";
import ModalRegister from "../../components/modal/ModalRegister";
import Map from "../../components/mapComponents/Map";
import PrivacyCheckboxComponents from "../../components/privacyPolicyComponents/PrivacyCheckboxComponents";

// Add interface for InputField props
interface InputFieldProps {
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    error?: string;
    type?: string;
    placeholder?: string;
    className?: string;
}

// Modified input field component to show error state
const InputField = memo(({ 
    label, 
    value, 
    onChange, 
    error, 
    type = "text", 
    placeholder,
    className = "" 
}: InputFieldProps) => {
    // Handle input change directly
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e);
    };

    return (
        <div className="mt-4">
            <label className="text-sm font-medium text-gray-700 font-poppins">{label} *</label>
            <input
                type={type}
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                className={`w-full h-10 px-4 border font-poppins rounded-lg mt-1 text-sm focus:ring focus:ring-indigo-300 
                    ${error ? 'border-red-500' : 'border-gray-300'} ${className}`}
            />
            {error && (
                <p className="mt-1 text-xs text-red-500 font-poppins">{error}</p>
            )}
        </div>
    );
});

const RegistrationPages: React.FC = () => {
    const defaultCategory: CategoryProduct = {
        id: 1,
        slug: "broadband-internet",
        is_price: false,
        category: "Broadband Internet",
        short_description: "",
        long_description: "",
        background_image: "",
        banner_products: "",
        products: [],
        benefits: []
    };

    const [fullname, setFullname] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [detailAddress, setDetailAddress] = useState<string>("");
    const [referralCode, setReferralCode] = useState<string>("");
    const [categorySlug, setCategorySlug] = useState<string>("");
    const [isPrice, setIsPrice] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<CategoryProduct>(defaultCategory);
    const [productItems, setProductItems] = useState<ProductItem[]>([]);
    const [productId, setProductId] = useState<number>(0);
    const [regionId, setRegionId] = useState<string>("");
    const [productPrice, setProductPrice] = useState<number>(0);
    const [totalTransaction, setTotalTransaction] = useState<number>(0);
    const [isModalOpen, setModalOpen] = useState(false);
    const [categories, setCategories] = useState<CategoryProduct[]>([]);
    const [loading, setLoading] = useState(true); // State for loading
    const [error, setError] = useState<string | null>(null); // State for errors
    const navigate = useNavigate();
    const [userLocation, setUserLocation] = useState<[number, number]>();
    const [locationError, setLocationError] = useState<string>('');
    const defaultCenter: [number, number] = [-6.200000, 106.816666]; // Jakarta coordinates
    const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

    const handleButtonClick = async () => {
        const payload: PayloadRegister = {
            customer_name: fullname,
            customer_phone: phoneNumber,
            customer_address: detailAddress,
            email: email,
            referral_code: referralCode,
            region_id: regionId,
            product_id: productId,
            product_category_id: selectedCategory.id,
            latitude: userLocation?.[0] ?? defaultCenter[0],
            longitude: userLocation?.[1] ?? defaultCenter[1]
        };

        try {
            const response = await sendRegister(payload);
            if (response.status === "success") {
                setModalOpen(true);
            } else {
                alert("Gagal melakukan pendaftaran.");
            }
        } catch {
            console.error("Error submitting data:", error);
            alert("Gagal melakukan pendaftaran.");
        }
    };

    const handleCloseButton = () => {
        navigate('/');
    };

    const handleLogoClick = () => {
        navigate('/');
    };

    const handleCategoryClick = (categorySlug: string, productItem: ProductItem[], category: CategoryProduct) => {
        setCategorySlug(categorySlug);
        setProductItems(productItem);
        setSelectedCategory(category);
        setIsPrice(category.is_price);
    };

    const productClick = (productId: number, price: number) => {
        setProductId(productId);

        setProductPrice(Number(price));

        let total = Number(price);
        setTotalTransaction(total);
    };

    const handleRegionClick = (regionId: string) => {
        setRegionId(regionId);
    }

    // Validation state
    const [formErrors, setFormErrors] = useState<{
        fullname?: string;
        email?: string;
        phoneNumber?: string;
        detailAddress?: string;
        regionId?: string;
        productId?: string;
        privacy?:string;
    }>({});

    // Check if form is valid
    const isFormValid = useMemo(() => {
        // Basic email validation regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        // Validate all required fields
        const isValidEmail = email.trim() !== '' && emailRegex.test(email);
        const isValidPhone = phoneNumber.trim() !== '' && phoneNumber.length >= 9;
        const isValidName = fullname.trim() !== '';
        const isValidAddress = detailAddress.trim() !== '';
        const isValidRegion = categorySlug === "broadband-internet" ? regionId !== '' : true;
        const isValidProduct = productId !== 0;
        const isPrivacyAccepted = acceptedPrivacy; // Changed this line

        // Update error messages
        const errors: typeof formErrors = {};
        if (!isValidName) errors.fullname = 'Nama lengkap wajib diisi';
        if (!isValidEmail) errors.email = 'Email tidak valid';
        if (!isValidPhone) errors.phoneNumber = 'Nomor telepon tidak valid';
        if (!isValidAddress) errors.detailAddress = 'Alamat wajib diisi';
        if (!isValidRegion && categorySlug === "broadband-internet") {
            errors.regionId = 'Lokasi wajib dipilih';
        }
        if (!isValidProduct) errors.productId = 'Paket wajib dipilih';
        if (!isPrivacyAccepted) errors.privacy = 'Anda harus menyetujui Kebijakan Privasi'; // Optional: add privacy error

        setFormErrors(errors);

        return (
            isValidEmail &&
            isValidPhone &&
            isValidName &&
            isValidAddress &&
            isValidRegion &&
            isValidProduct &&
            isPrivacyAccepted // Changed this line
        );
    }, [fullname, email, phoneNumber, detailAddress, regionId, productId, categorySlug, acceptedPrivacy]); // Added acceptedPrivacy to dependencies

    // Memoize the change handlers
    const handleFullnameChange = useMemo(() => (
        (e: React.ChangeEvent<HTMLInputElement>) => setFullname(e.target.value)
    ), []);

    const handleEmailChange = useMemo(() => (
        (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)
    ), []);

    const handlePhoneChange = useMemo(() => (
        (e: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)
    ), []);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categoriesData = await fetchCategory();
                                
                // Filter to get only the category with slug 'sudirman-park'
                const sudirmanParkCategory = categoriesData.filter(element => 
                    element.slug !== 'sudirman-park'
                );
                
                // Only set state if the category exists
                if (sudirmanParkCategory.length > 0) {
                    setCategories(sudirmanParkCategory);
                }
            } catch (err) {
                console.error("Failed to fetch category product:", err);
                setError("Failed to load category product. Please try again later.");
            } finally {
                setLoading(false); // End loading
            }
        };

        setTimeout(() => setLoading(false), 2000);
        fetchCategories();
    }, []); // Runs only once on component mount

    useEffect(() => {
        // Function to get user's location
        const getCurrentLocation = () => {
            if (!navigator.geolocation) {
                setLocationError('Geolocation is not supported by your browser');
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setUserLocation([latitude, longitude]);
                },
                (error) => {
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            setLocationError('Location permission denied');
                            break;
                        case error.POSITION_UNAVAILABLE:
                            setLocationError('Location information unavailable');
                            break;
                        case error.TIMEOUT:
                            setLocationError('Location request timed out');
                            break;
                        default:
                            setLocationError('An unknown error occurred');
                    }
                },
                {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0
                }
            );
        };

        // Request location immediately when component mounts
        getCurrentLocation();
    }, []); // Empty dependency array means this runs once on mount

    if (loading) {
        return (
            <SpinnerLoading/>
        );
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div className="flex flex-col w-full min-h-screen bg-gray-50">
            <ModalRegister isOpen={isModalOpen} />
            {/* Logo */}
            <img src={logoLM} alt="Logo LM" className="w-32 h-11 md:w-40 md:h-14 mt-6 mx-8 md:mx-40 cursor-pointer" onClick={handleLogoClick} />

            <div className="flex flex-col items-center w-full min-h-screen bg-gray-50">
                {/* Modal Content */}
                <div className="flex flex-col bg-white mx-8 md:w-3/4 md:mx-24 my-6 p-6 rounded-lg shadow-lg">
                    <button 
                        className="text-xl absolute top-4 right-8 md:right-40 md:mx-2 text-gray-600 hover:text-gray-800" 
                        onClick={handleCloseButton}>
                        &times;
                    </button>

                    <h2 className="text-2xl font-semibold text-gray-800 font-poppins">Lengkapi Data Diri Anda</h2>
                    <p className="text-sm text-gray-500 mt-2 font-poppins">
                        Semua data ini wajib di isi untuk kebutuhan proses pemesanan kamu
                    </p>

                    <InputField
                        label="Nama Lengkap"
                        value={fullname}
                        onChange={handleFullnameChange}
                        error={formErrors.fullname}
                        placeholder="Nama lengkap anda"
                    />

                    <div className="mt-4">
                        <label className="text-sm font-medium text-gray-700 font-poppins">Nomor Whatsapp *</label>
                        <div className="flex items-center border rounded-lg mt-1">
                            <span className="px-3 text-gray-700">+62</span>
                            <input
                                type="text"
                                value={phoneNumber}
                                onChange={handlePhoneChange}
                                placeholder="812 345 678"
                                className={`w-full h-10 px-2 text-sm border-l focus:ring focus:ring-indigo-300 
                                    ${formErrors.phoneNumber ? 'border-red-500' : 'border-gray-300'}`}
                            />
                        </div>
                        {formErrors.phoneNumber && (
                            <p className="mt-1 text-xs text-red-500">{formErrors.phoneNumber}</p>
                        )}
                    </div>

                    <InputField
                        label="Email"
                        value={email}
                        onChange={handleEmailChange}
                        error={formErrors.email}
                        placeholder="emailmu@domain.com"
                    />

                    <label className="text-sm text-gray-500 italic mt-2 font-poppins">
                        Pastikan email kamu aktif untuk cek pesanan
                    </label>

                    {/* Tabs Component */}
                    <label className="mt-4 text-sm font-medium text-gray-700 font-poppins">Pilih Kategori Produk *</label>
                    <TabsComponent onCategoryChange={handleCategoryClick} categories={categories} />

                    {[true].includes(isPrice) && (
                        <>
                            <label className="mt-4 text-sm font-medium text-gray-700 font-poppins">Lokasi Anda :</label>
                            <CascadingDropdown onRegionChange={handleRegionClick} />
                            
                            <Map 
                                center={userLocation ?? defaultCenter} 
                                zoom={13}
                                locationError={locationError}
                            />

                            <label className="mt-4 text-sm font-medium text-gray-700 font-poppins">Detail Lokasi :</label>
                            <textarea
                                placeholder="Nama Jalan, RT & RW"
                                value={detailAddress}
                                onChange={(e) => setDetailAddress(e.target.value)}
                                className="w-full h-24 px-4 py-2 border rounded-lg text-sm focus:ring focus:ring-indigo-300 font-poppins"
                            />

                            <label className="mt-4 text-sm font-medium text-gray-700 font-poppins">Kode Referral</label>
                            <input
                                type="text"
                                placeholder="LM000xx"
                                value={referralCode}
                                onChange={(e) => setReferralCode(e.target.value)}
                                className="w-full h-10 px-4 border rounded-lg mt-1 text-sm focus:ring focus:ring-indigo-300 font-poppins"
                            />

                            <label className="mt-4 text-sm font-medium text-gray-700 font-poppins">Pilih Paket *</label>
                            <ListProductsRegister onProductId={productClick} category={selectedCategory} />

                            {/* Total Section */}
                            <div className="mt-4 bg-gray-100 p-4 rounded-lg">
                                <div className="flex justify-between text-sm font-poppins">
                                    <span>Harga Paket</span>
                                    <span>Rp {new Intl.NumberFormat("id-ID", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(productPrice)}</span>
                                </div>
                                <div className="flex justify-between text-sm mt-2 text-red-500 line-through font-poppins">
                                    <span>Biaya Instalasi</span>
                                    <span>Rp 250.000</span>
                                </div>
                                <hr className="my-2 border-t border-gray-300" />
                                <div className="flex justify-between text-sm font-semibold font-poppins">
                                    <span>Total</span>
                                    <span>Rp {new Intl.NumberFormat("id-ID", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalTransaction)}</span>
                                </div>
                            </div>
                        </>
                    )}

                    {[false].includes(isPrice) && (
                        <>
                            <Map 
                                center={userLocation ?? defaultCenter} 
                                zoom={13}
                                locationError={locationError}
                            />
                            <label className="mt-4 text-sm font-medium text-gray-700 font-poppins">Alamat :</label>
                            <textarea
                                placeholder="Nama Jalan, RT & RW"
                                value={detailAddress}
                                onChange={(e) => setDetailAddress(e.target.value)}
                                className="w-full h-24 px-4 py-2 border rounded-lg text-sm focus:ring focus:ring-indigo-300 font-poppins"
                            />
                            <label className="text-sm text-gray-500 italic mt-2 font-poppins">
                                Pastikan alamat sesuai dengan lokasi pemasangan, guna tim kami untuk mengecek coverage area
                            </label>
                            <label className="mt-4 text-sm font-medium text-gray-700 font-poppins">Pilih Paket *</label>
                            <ListProductsRegister onProductId={productClick} category={selectedCategory} />
                        </>
                    )}

                    <PrivacyCheckboxComponents
                        checked={acceptedPrivacy}
                        onChange={setAcceptedPrivacy}
                    />

                    {/* Submit Button */}
                    <div className="flex justify-end mt-6">
                        <button 
                            onClick={handleButtonClick}
                            disabled={!isFormValid}
                            className={`px-6 py-2 rounded-lg font-semibold font-poppins transition
                                ${isFormValid 
                                    ? 'bg-pink-600 hover:bg-pink-700 text-white cursor-pointer' 
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                        >
                            Kirim
                        </button>
                    </div>
                </div>
            </div>
            
        </div>
    );
};

export default RegistrationPages;